import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, CloudUpload, XCircle, CheckCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext"; // 🔥 Dark mode támogatás

const EditCategoryModal = ({ isOpen, onClose, category, updateCategory }) => {
  const [successMessage, setSuccessMessage] = useState("");
  const { darkMode } = useTheme(); // 🔥 Témavezérlés

  const [categoryData, setCategoryData] = useState({
    name: category?.name || "",
    imageFile: null,
    imageUrl: category?.imageUrl || null,
    isImageDeleted: false,
  });

  useEffect(() => {
    if (category) {
      setCategoryData({
        name: category.name || "",
        imageFile: null,
        imageUrl: category.imageUrl || null,
        isImageDeleted: false,
      });
    }
  }, [category]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryData((prev) => ({
        ...prev,
        imageFile: file,
        imageUrl: URL.createObjectURL(file),
        isImageDeleted: false,
      }));
    }
  };

  const handleRemoveImage = () => {
    setCategoryData((prev) => ({
      ...prev,
      imageFile: null,
      imageUrl: null,
      isImageDeleted: true,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!categoryData.name.trim()) return;

    updateCategory(category.id, categoryData);

    setSuccessMessage("✅ Categoria a fost modificată cu succes!");
    setTimeout(() => {
      setSuccessMessage("");
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* ✅ Sikerüzenet */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="fixed top-4 left-[39%] transform -translate-x-1/2 bg-green-500 text-white px-10 py-8 rounded-3xl shadow-lg text-center z-50"
          >
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ Háttér overlay */}
      <div
        className={`fixed inset-0 flex items-center justify-center bg-opacity-80 ${
          darkMode ? "bg-black" : "bg-gray-500"
        }`}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* ✅ Animált Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`p-6 rounded-3xl shadow-xl w-[500px] relative ${
            darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ❌ Bezárás gomb */}
          <button
            onClick={onClose}
            className="absolute top-2 right-3 text-gray-400 hover:text-red-500 transition text-2xl"
          >
            ✖
          </button>

          <h3
            className={`text-2xl font-semibold mb-4 text-center ${
              darkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            ✏️ Editează categoria
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* ✅ Kategória név */}
            <label className="block font-semibold">Numele categoriei:</label>
            <input
              type="text"
              value={categoryData.name}
              onChange={(e) =>
                setCategoryData((prev) => ({ ...prev, name: e.target.value }))
              }
              className={`w-full p-3 border rounded-3xl ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
              }`}
              required
            />

            {/* ✅ Kép feltöltése */}
            <label className="block font-semibold">Imagine:</label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="fileInput"
              />
              <label
                htmlFor="fileInput"
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-3xl cursor-pointer transition ${
                  darkMode
                    ? "bg-blue-700 hover:bg-blue-600 text-white"
                    : "bg-blue-600 hover:bg-blue-500 text-white"
                }`}
              >
                <CloudUpload size={25} />
                <span className="text-lg font-semibold">
                  {categoryData.imageFile ? "Schimbă imaginea" : "Alege fișier"}
                </span>
              </label>
            </div>

            {/* ✅ Előnézet (ha van kép) */}
            {(categoryData.imageUrl || categoryData.imageFile) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="relative mt-4"
              >
                <img
                  src={categoryData.imageUrl}
                  alt="Previzualizare"
                  className="w-full h-40 object-cover rounded shadow-md"
                />
                {/* ✅ Kép eltávolítása gomb */}
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className={`absolute top-2 right-2 p-1 rounded-full transition ${
                    darkMode
                      ? "bg-red-700 hover:bg-red-600 text-white"
                      : "bg-red-600 hover:bg-red-500 text-white"
                  }`}
                >
                  <Trash2 size={18} />
                </button>
              </motion.div>
            )}

            {/* ✅ Gombok */}
            <div className="flex justify-between mt-4 gap-4">
              <button
                type="button"
                onClick={onClose}
                className={`w-full px-5 py-4 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${
                  darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-500 text-white hover:bg-gray-400"
                }`}
              >
                <XCircle size={24} /> Anulare
              </button>

              <button
                type="submit"
                className={`w-full px-5 py-4 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${
                  darkMode
                    ? "bg-green-700 text-white hover:bg-green-600"
                    : "bg-green-600 text-white hover:bg-green-500"
                }`}
              >
                <CheckCircle size={24} /> Salvează
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default EditCategoryModal;
